import { useState } from "react";
import { Link } from "react-router-dom";
import { MathText } from "./MathText.js";
import { DEMO_ITEMS } from "../lib/demo-items.js";
import { moduleLabel } from "../lib/labels.js";

/**
 * Live trap demo, embedded on the marketing landing page.
 *
 * The landing page's job is to show the one thing no competitor does: naming
 * the pattern behind the answer you picked. Describing that in prose asks the
 * visitor to take it on faith. So this section IS the product moment: pick a
 * choice, see the answer, and see why every wrong choice was built to look
 * right. /try continues with four more.
 *
 * Uses the real audited corpus item (sl-1103, the wrong-question trap) from the
 * same source as the /try demo, so there is no marketing mock to drift.
 *
 * Voice rules (public surface): plain English, no em-dashes, no internal
 * vocabulary. Module names go through moduleLabel().
 */

/** The wrong-question math trap: shortest to read, biggest "oh" on reveal. */
const ITEM = DEMO_ITEMS.find((i) => i.id === "sl-1103") ?? DEMO_ITEMS[0]!;

export function TrapDemo() {
  const [picked, setPicked] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const gotIt = picked === ITEM.correctId;

  return (
    <section className="bht-section">
      <div style={{ maxWidth: 1080, margin: "0 auto" }} className="bht-demo-grid">
        <div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--accent-amber)",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            Try one now
          </div>
          <h2
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 32,
              fontWeight: 600,
              color: "var(--fg-0)",
              margin: 0,
              lineHeight: 1.2,
              letterSpacing: "-0.015em",
            }}
            className="bht-section-headline"
          >
            The wrong answers are designed. Your review should be too.
          </h2>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              color: "var(--fg-2)",
              lineHeight: 1.6,
              margin: "16px 0 0",
            }}
          >
            Most explanations tell you why the right answer is right. Here is the
            other half: why the answer you reached for looked right, and the
            pattern behind it.
          </p>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              color: "var(--fg-2)",
              lineHeight: 1.6,
              margin: "14px 0 0",
            }}
          >
            This is a real question from the app, not a mock. Pick a choice and
            see what happens.
          </p>
        </div>

        <div className="bht-demo-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              paddingBottom: 12,
              marginBottom: 16,
              borderBottom: "1px solid var(--border-line)",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            <span style={{ color: "var(--fg-3)" }}>
              {moduleLabel(ITEM.module)} · digital SAT
            </span>
            {/* The trap name is a spoiler, so it only appears on reveal. */}
            <span style={{ color: revealed ? "var(--signal-red)" : "var(--fg-3)" }}>
              {revealed ? ITEM.skillLabel : "Real question"}
            </span>
          </div>

          <MathText
            as="p"
            text={ITEM.passage}
            style={{
              color: "var(--fg-1)",
              fontSize: 16,
              lineHeight: 1.6,
              margin: "0 0 14px",
            }}
          />
          <MathText
            as="p"
            text={ITEM.prompt}
            style={{
              color: "var(--fg-0)",
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.5,
              margin: "0 0 16px",
            }}
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ITEM.choices.map((c) => {
              const isPicked = picked === c.id;
              const isCorrect = c.id === ITEM.correctId;
              let borderColor = "var(--border-line)";
              let bg = "var(--bg-2)";
              if (revealed && isCorrect) {
                borderColor = "var(--signal-green)";
                bg = "var(--signal-green-12)";
              } else if (revealed && isPicked) {
                borderColor = "var(--signal-red)";
                bg = "var(--signal-red-12)";
              } else if (isPicked) {
                borderColor = "var(--accent-amber)";
              }
              return (
                <div key={c.id}>
                  <button
                    type="button"
                    onClick={() => !revealed && setPicked(c.id)}
                    disabled={revealed}
                    aria-pressed={isPicked}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      minHeight: 48,
                      padding: "12px 14px",
                      background: bg,
                      border: `1.5px solid ${borderColor}`,
                      borderRadius: 8,
                      color: "var(--fg-0)",
                      fontSize: 15,
                      lineHeight: 1.45,
                      cursor: revealed ? "default" : "pointer",
                      fontFamily: "var(--font-sans)",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontWeight: 700,
                        color: revealed
                          ? isCorrect
                            ? "var(--signal-green)"
                            : isPicked
                              ? "var(--signal-red)"
                              : "var(--fg-3)"
                          : "var(--fg-2)",
                        flexShrink: 0,
                        width: 14,
                      }}
                    >
                      {c.id}
                    </span>
                    <MathText as="span" text={c.text} style={{ flex: 1 }} />
                    {/* Correctness is never carried by color alone: the state is
                        spelled out in words next to the choice. */}
                    {revealed && (isCorrect || isPicked) && (
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: isCorrect ? "var(--signal-green)" : "var(--signal-red)",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isCorrect ? "✓ Correct" : "× You picked"}
                      </span>
                    )}
                  </button>
                  {revealed && !isCorrect && c.trap && (
                    <p
                      style={{
                        margin: "6px 0 0 40px",
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: "var(--fg-2)",
                      }}
                    >
                      <span style={{ color: "var(--signal-red)", fontWeight: 600 }}>
                        The trap:{" "}
                      </span>
                      {c.trap}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Result announcement: polite so it does not interrupt a keyboard
              user mid-action, and it carries the whole teach in one string. */}
          <div aria-live="polite" style={{ display: revealed ? "block" : "none" }}>
            {revealed && (
              <div
                style={{
                  marginTop: 18,
                  padding: "14px 16px",
                  background: "var(--bg-2)",
                  borderRadius: 8,
                  borderLeft: `3px solid ${gotIt ? "var(--signal-green)" : "var(--accent-amber)"}`,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: gotIt ? "var(--signal-green)" : "var(--accent-amber)",
                  }}
                >
                  {gotIt ? "You caught it" : "That one got you"}
                </p>
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: "var(--fg-1)",
                  }}
                >
                  {ITEM.correctNote}
                </p>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 20,
              display: "flex",
              gap: 14,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {!revealed ? (
              <button
                type="button"
                onClick={() => picked && setRevealed(true)}
                disabled={!picked}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 48,
                  padding: "13px 22px",
                  background: picked ? "var(--accent-amber)" : "var(--bg-2)",
                  color: picked ? "var(--fg-on-accent)" : "var(--fg-3)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: "none",
                  cursor: picked ? "pointer" : "default",
                }}
              >
                See the answer
              </button>
            ) : (
              <Link
                to="/try"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 48,
                  padding: "13px 22px",
                  background: "var(--accent-amber)",
                  color: "var(--fg-on-accent)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Try four more, no sign-up
              </Link>
            )}
            {revealed && (
              <span
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: 13,
                  color: "var(--fg-3)",
                  lineHeight: 1.5,
                  flex: 1,
                  minWidth: 180,
                }}
              >
                The app does this on every question, and tracks which traps keep
                working on you.
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
